import { Box, Chip, Stack, Typography } from "@mui/material"
import { FC } from "react"
import {
  ReservationSyncDto,
  SourceKeyDto,
  SourceReadDto,
} from "src/features/things/reservationSyncApi"
import { fmtCount } from "./ReservationSyncChrome"

/**
 * The four source cards (LOCKER-421 `DoD2`, `DoD8`).
 *
 * Today's page shows two numbers — "partner rows" and "our rows" — and one read
 * state. Four sources need four, and they need their own captions, because **the
 * same read-state word means something different on each source**:
 *
 *  - `UNVERIFIED` on **v1** means the partner withheld a `total` it usually sends;
 *  - `UNVERIFIED` on **v2** is the permanent ceiling — no v2 endpoint reports a
 *    per-locker total at all, so `COMPLETE` is unreachable there by construction;
 *  - `COMPLETE` on **DynamoDB** means a partition walked to exhaustion;
 *  - `COMPLETE` on **OLAP** means one `SELECT` returned, and says nothing about
 *    whether the projection is right — that is what the drift classes are for.
 *
 * A single rolled-up chip cannot carry any of that, which is why the caption is
 * part of the card rather than a tooltip.
 */

const SOURCE_TITLE: Record<SourceKeyDto, string> = {
  v1: "SST V1 · PARTNER",
  v2: "SST V2 · PARTNER",
  ddb: "DYNAMODB · TRANSACTIONAL",
  olap: "SL2 OLAP · POSTGRES",
}

/**
 * Authority ordering, left to right, and it is not cosmetic: it is the order that
 * decides who wins a disagreement. Both partner APIs are the source of truth;
 * DynamoDB is the transactional store and the only thing ever written; OLAP is
 * downstream of the DynamoDB stream and never written by this tooling.
 */
export const SOURCE_ORDER: SourceKeyDto[] = ["v1", "v2", "ddb", "olap"]

const ACCENT: Record<SourceKeyDto, string> = {
  v1: "#2D5F8A",
  v2: "#7A4FA3",
  ddb: "#1A1917",
  olap: "#2E6B4F",
}

const READ_STATE_COLOR: Record<
  SourceReadDto["readState"],
  "success" | "warning" | "error" | "default"
> = {
  COMPLETE: "success",
  UNVERIFIED: "warning",
  TRUNCATED: "error",
  FAILED: "error",
  NOT_APPLICABLE: "default",
}

const SourceCard: FC<{
  sourceKey: SourceKeyDto
  read: SourceReadDto
  /** The v1/v2 snapshot split, shown under the DynamoDB count (`DoD3`). */
  subLine?: string
}> = ({ sourceKey, read, subLine }) => {
  const absent = read.readState === "NOT_APPLICABLE" || read.fetched === null
  const secondary =
    subLine ??
    (read.error
      ? read.error
      : read.totalReported !== null
        ? `of ${fmtCount(read.totalReported)} reported`
        : read.readState === "NOT_APPLICABLE"
          ? "nothing expected"
          : "no total reported")

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderTop: 3,
        borderTopColor: ACCENT[sourceKey],
        borderRadius: 1,
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: ACCENT[sourceKey],
        }}
      >
        {SOURCE_TITLE[sourceKey]}
      </Typography>
      <Typography
        sx={{
          fontFamily: "monospace",
          fontSize: "1.6rem",
          lineHeight: 1.1,
          // An em dash, never a zero: a source that was not read has no count, and
          // a 0 there would read as "this source holds nothing".
          color: absent ? "text.disabled" : "text.primary",
        }}
      >
        {fmtCount(read.fetched)}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: read.error ? "error.main" : "text.secondary",
          fontWeight: read.error ? 600 : 400,
        }}
      >
        {secondary}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        <Chip
          size="small"
          label={read.readState === "NOT_APPLICABLE" ? "N/A" : read.readState}
          color={READ_STATE_COLOR[read.readState]}
          variant={read.readState === "COMPLETE" ? "filled" : "outlined"}
          sx={{ fontWeight: 700, letterSpacing: "0.06em", height: 20 }}
        />
        {/* A capture's drift includes whatever moved after it was taken, so a
            snapshot-fed card must not look like a live read. */}
        {read.fromSnapshot && (
          <Chip
            size="small"
            data-testid={`source-${sourceKey}-snapshot`}
            // The age is frozen at run time, so it says so: "3.0h old" on a run
            // opened a week later would read as the capture's age now.
            label={`SNAPSHOT · ${read.fromSnapshot.ageHours.toFixed(1)}h old at run`}
            title={`Read from an on-disk capture taken ${read.fromSnapshot.fetchedAtIso}, not live`}
            variant="outlined"
            sx={{ fontWeight: 700, letterSpacing: "0.06em", height: 20 }}
          />
        )}
      </Box>
      {!!read.caption && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            lineHeight: 1.45,
            borderTop: 1,
            borderColor: "divider",
            pt: 1,
          }}
        >
          {read.caption}
        </Typography>
      )}
    </Box>
  )
}

export const SourceCards: FC<{ run: ReservationSyncDto }> = ({ run }) => {
  if (!run.sources)
    // A run stored before LOCKER-421 has one rolled-up read state and nothing to
    // split into four. Showing four cards with three invented states would be a
    // claim about reads that were never made.
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        This run predates the four-way recon — it recorded one rolled-up read state
        (<code>{run.readState.toLowerCase()}</code>) rather than one per source.
        Re-run the sweep to get the per-source breakdown.
      </Typography>
    )

  const ddbSubLine = run.snapshotShape
    ? `${fmtCount(run.snapshotShape.v1Shaped)} v1-shaped · ${fmtCount(run.snapshotShape.v2Shaped)} v2-shaped`
    : undefined

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(4, minmax(0, 1fr))",
        },
        gap: 2,
      }}
    >
      {SOURCE_ORDER.map((key) => (
        <SourceCard
          key={key}
          sourceKey={key}
          read={run.sources![key]}
          subLine={key === "ddb" ? ddbSubLine : undefined}
        />
      ))}
    </Box>
  )
}

/**
 * The compact strip the drift tab carries, where the four full cards would push
 * the table below the fold.
 *
 * Same four sources, same read states, no captions — the overview tab is where the
 * captions live, and repeating them here would make the table's header taller than
 * the table.
 */
export const SourceStrip: FC<{ run: ReservationSyncDto }> = ({ run }) => {
  if (!run.sources) return null
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          md: "repeat(4, minmax(0, 1fr))",
        },
        gap: 1.5,
      }}
    >
      {SOURCE_ORDER.map((key) => {
        const read = run.sources![key]
        return (
          <Stack
            key={key}
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.25 }}
          >
            <Box
              sx={{
                width: 3,
                alignSelf: "stretch",
                bgcolor: ACCENT[key],
                borderRadius: 1,
              }}
            />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, letterSpacing: "0.07em", color: ACCENT[key] }}
              >
                {SOURCE_TITLE[key].split(" · ")[0]}
              </Typography>
              <Typography sx={{ fontFamily: "monospace", fontSize: "1.1rem" }}>
                {fmtCount(read.fetched)}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={read.readState === "NOT_APPLICABLE" ? "N/A" : read.readState}
              color={READ_STATE_COLOR[read.readState]}
              variant={read.readState === "COMPLETE" ? "filled" : "outlined"}
              sx={{ fontWeight: 700, letterSpacing: "0.06em", height: 20 }}
            />
          </Stack>
        )
      })}
    </Box>
  )
}
